import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { brandConfig } from '../../config/brands';
import type { SurveyQuestionKey } from '../../config/brands/types';
import type { SurveyConfigResponse } from '../../hooks/useUserProfile';
import { useSurveyModalStore } from '../../stores/surveyModalStore';
import './AttributeCollectionModal.css';

type AttributeQuestion = SurveyQuestionKey;

type Props = {
  question: AttributeQuestion;
  isOpen: boolean;
  /** パートナー別の設問構成 (選択肢・付与額)。DB 駆動設問 (occupation) の選択肢に使う。 */
  surveyConfig?: SurveyConfigResponse | null;
  onAnswer: (question: AttributeQuestion, answer: string | string[]) => void;
  onSkip: () => void;
  isSubmitting?: boolean;
  creditsGranted?: number | null;
};

// 固定設問のラベル定義。DB 駆動設問 (occupation) は surveyConfig 由来の選択肢を使う。
const questionConfig: Record<string, {
  title: string;
  options: { value: string; label: string }[];
}> = {
  user_type: {
    title: 'ご利用形態を教えてください',
    options: [
      { value: 'corporate', label: '仕事のために使いたい' },
      { value: 'individual', label: '個人で楽しむために使いたい' },
    ],
  },
  gender: {
    title: '性別を教えてください',
    options: [
      { value: 'male', label: '男性' },
      { value: 'female', label: '女性' },
    ],
  },
  occupation: {
    title: '職業を教えてください',
    options: [],
  },
  usage_purpose: {
    title: 'ご利用の目的を教えてください（複数選択可）',
    options: [],
  },
  discovery_channel: {
    title: 'SAITEを知ったきっかけを教えてください（複数選択可）',
    options: [],
  },
};

const EARNED_DISPLAY_MS = 3000;

export function AttributeCollectionModal({ question, isOpen, surveyConfig = null, onAnswer, onSkip, isSubmitting = false, creditsGranted = null }: Props) {
  const [showEarned, setShowEarned] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const prevCreditsGrantedRef = useRef(creditsGranted);

  // 設問が切り替わったら複数選択の選択状態をリセットする。
  useEffect(() => {
    setSelected([]);
  }, [question]);

  // creditsGranted が null から値に変わったら（ミューテーション完了時）3秒間メッセージを表示
  useEffect(() => {
    if (prevCreditsGrantedRef.current === null && creditsGranted !== null) {
      setShowEarned(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowEarned(false), EARNED_DISPLAY_MS);
    }
    prevCreditsGrantedRef.current = creditsGranted;
  }, [creditsGranted]);

  // アンマウント時のみタイマーをクリア
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // モーダルの表示状態 (質問中 or 獲得演出中) をストアへ反映する。
  // ナビゲーションガイドが「アンケートが閉じ切ってから」起動できるようにするため。
  const modalVisible = isOpen || showEarned;
  const setSurveyModalVisible = useSurveyModalStore((s) => s.setVisible);
  useEffect(() => {
    setSurveyModalVisible(modalVisible);
  }, [modalVisible, setSurveyModalVisible]);
  useEffect(() => () => setSurveyModalVisible(false), [setSurveyModalVisible]);

  if (!isOpen && !showEarned) return null;

  const staticConfig = questionConfig[question] ?? { title: '', options: [] };
  // DB 駆動設問 (occupation 等) は surveyConfig の選択肢を使う。無ければ固定定義。
  const surveyQuestion = surveyConfig?.questions.find((q) => q.key === question);
  // 固定設問 (user_type / gender) の選択肢は API が value のみを返すため、
  // ラベルは questionConfig 側で補完する (値コードがそのまま表示されるのを防ぐ)。
  const staticLabels = new Map(staticConfig.options.map((o) => [o.value, o.label]));
  const options = (surveyQuestion?.options && surveyQuestion.options.length > 0)
    ? surveyQuestion.options.map((o) => ({ value: o.value, label: o.label ?? staticLabels.get(o.value) ?? o.value }))
    : staticConfig.options;
  const title = staticConfig.title || surveyQuestion?.label || '';
  // 複数選択設問はチェックボックス一覧 + 「回答する」ボタンで送信する。
  const isMulti = !!surveyQuestion?.multi;
  const toggleSelected = (value: string) => {
    setSelected((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  };

  // 付与額 (円相当) と組織上限。surveyConfig があればそれを、無ければ従来の 150/300。
  const rewardYen = surveyConfig?.credits_per_answer ?? 150;
  const maxCredits = surveyConfig?.max_credits_per_org ?? 300;
  // 設問が1問だけのパートナー (usodeki) は「1問につき」を付けない。
  const singleQuestion = (surveyConfig?.questions.length ?? 2) <= 1;
  // 組織概念を隠すブランド (autoPersonalWorkspace) では組織単位の上限注記を出さない。
  const hideOrgNote = brandConfig.features.autoPersonalWorkspace ?? false;

  const buttonsDisabled = isSubmitting || showEarned;

  return createPortal(
    <div className="attr-modal-overlay">
      <div className="attr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="attr-modal-header">
          <h2 className="attr-modal-title">簡単な質問にご回答ください</h2>
          <p className="attr-modal-subtitle">
            ご回答いただくと、{singleQuestion ? '' : '1問につき'}<span className="attr-points-highlight">{rewardYen}円相当のポイント</span>をプレゼント！
          </p>
          {!hideOrgNote && (
            <p className="attr-modal-note">※1組織あたり最大{maxCredits}ポイントまで獲得できます</p>
          )}
        </div>

        <div className="attr-modal-content">
          <div className="attr-question">
            <label className="attr-question-label">{title}</label>
            {isMulti ? (
              <>
                <div className="attr-options">
                  {options.map((opt) => {
                    const checked = selected.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`attr-option-btn attr-option-checkbox${checked ? ' is-checked' : ''}`}
                        onClick={() => toggleSelected(opt.value)}
                        disabled={buttonsDisabled}
                        aria-pressed={checked}
                      >
                        <span className="attr-checkbox-box" aria-hidden="true">{checked ? '✓' : ''}</span>
                        <span className="attr-checkbox-label">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="attr-submit-btn"
                  onClick={() => onAnswer(question, selected)}
                  disabled={buttonsDisabled || selected.length === 0}
                >
                  回答する
                </button>
              </>
            ) : (
              <div className="attr-options">
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className="attr-option-btn"
                    onClick={() => onAnswer(question, opt.value)}
                    disabled={buttonsDisabled}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="attr-modal-actions">
          <button
            type="button"
            className="attr-skip-btn"
            onClick={onSkip}
            disabled={buttonsDisabled}
          >
            スキップ（ポイントは付与されません）
          </button>
        </div>

        {showEarned && (
          <div className="attr-loading-overlay">
            <div className="attr-earned-message">
              {creditsGranted != null && creditsGranted > 0 ? (
                <>
                  <img src="/money_bag_dollar.svg" alt="" className="attr-earned-coin" />
                  <span className="attr-earned-text">{creditsGranted}ポイントを獲得しました！</span>
                </>
              ) : (
                <span className="attr-earned-text">回答ありがとうございます！</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
